import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type AdminBoss,
  type AdminBossCompetitionConfig,
  type AdminBossCompetitionStatus,
  type AdminCosmetic,
  type SettingItem,
} from '@/api/client';
import { ErrorBox, Loading, PageTitle, fullW } from '@/components/ui';

type BossDraft = Omit<AdminBoss, 'rewardFrame' | '_count'>;

const NUMBER_FIELDS: Array<{
  key: keyof BossDraft;
  label: string;
  step?: number;
}> = [
  { key: 'sortOrder', label: 'Sort order' },
  { key: 'durationMs', label: 'Score run duration (ms)', step: 1_000 },
  { key: 'normalHp', label: 'Normal HP' },
  { key: 'hardHp', label: 'Hard HP' },
  { key: 'gripMax', label: 'Grip max' },
  { key: 'staggerMax', label: 'Break max' },
  { key: 'attackIntervalNormalMs', label: 'Normal attack interval (ms)', step: 100 },
  { key: 'attackIntervalHardMs', label: 'Hard attack interval (ms)', step: 100 },
  { key: 'attackDamageNormal', label: 'Normal attack damage' },
  { key: 'attackDamageHard', label: 'Hard attack damage' },
  { key: 'wrongTapDamage', label: 'Wrong-tap damage' },
  { key: 'rewardCoinsNormal', label: 'Normal coins' },
  { key: 'rewardCoinsHard', label: 'Hard coins' },
  { key: 'rewardGemsFirstClear', label: 'First-clear gems' },
  { key: 'dailyRewardLimit', label: 'Daily rewarded wins' },
];

function toDraft(item: AdminBoss): BossDraft {
  const { rewardFrame: _rewardFrame, _count: _count, ...draft } = item;
  return draft;
}

function isBossRewardFrame(frame: AdminCosmetic): boolean {
  return frame.id.startsWith('boss-frame-') || frame.data?.source === 'boss';
}

export function BossesPage() {
  const [items, setItems] = useState<AdminBoss[] | null>(null);
  const [frames, setFrames] = useState<AdminCosmetic[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [competition, setCompetition] =
    useState<AdminBossCompetitionConfig | null>(null);
  const [competitionStatus, setCompetitionStatus] =
    useState<AdminBossCompetitionStatus | null>(null);
  const [drafts, setDrafts] = useState<Record<string, BossDraft>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    const [
      bossResponse,
      settingsResponse,
      cosmeticsResponse,
      competitionResponse,
    ] = await Promise.all([
      api.bosses(),
      api.settings(),
      api.cosmetics(),
      api.bossCompetition(),
    ]);
    setItems(bossResponse.items);
    setDrafts(
      Object.fromEntries(
        bossResponse.items.map((item) => [item.id, toDraft(item)]),
      ),
    );
    setEnabled(
      Boolean(
        settingsResponse.items.find(
          (setting: SettingItem) => setting.key === 'boss.enabled',
        )?.value,
      ),
    );
    setFrames(
      cosmeticsResponse.items.filter(
        (cosmetic) => cosmetic.slot === 'avatarFrame' && isBossRewardFrame(cosmetic),
      ),
    );
    setCompetition(competitionResponse.config);
    setCompetitionStatus(competitionResponse.status);
  };

  useEffect(() => {
    void load().catch((reason) => setError(String(reason)));
  }, []);

  const totalRuns = useMemo(
    () => items?.reduce((sum, item) => sum + item._count.runs, 0) ?? 0,
    [items],
  );

  if (error && !items) return <ErrorBox text={error} />;
  if (!items) return <Loading />;

  const updateDraft = <K extends keyof BossDraft>(
    id: string,
    key: K,
    value: BossDraft[K],
  ): void => {
    setDrafts((current) => ({
      ...current,
      [id]: { ...current[id]!, [key]: value },
    }));
  };

  const save = async (id: string): Promise<void> => {
    const draft = drafts[id];
    if (!draft) return;
    setSaving(id);
    setError(null);
    try {
      await api.patchBoss(id, draft);
      await load();
    } catch (reason) {
      setError(String(reason));
    } finally {
      setSaving(null);
    }
  };

  const create = async (): Promise<void> => {
    setSaving('new');
    const suffix = Date.now().toString(36);
    try {
      await api.createBoss({
        slug: `new-boss-${suffix}`,
        name: 'NEW BOSS',
        description: 'Configure this encounter before activating it.',
        imageUrl: null,
        accentColor: '#F4C724',
        isActive: false,
        sortOrder: items.length,
        scheduleDay: null,
        durationMs: 30_000,
        normalHp: 10_000,
        hardHp: 15_000,
        gripMax: 100,
        staggerMax: 100,
        attackIntervalNormalMs: 5_000,
        attackIntervalHardMs: 3_800,
        attackDamageNormal: 10,
        attackDamageHard: 14,
        wrongTapDamage: 18,
        rewardCoinsNormal: 100,
        rewardCoinsHard: 180,
        rewardGemsFirstClear: 2,
        dailyRewardLimit: 3,
        rewardFrameId: null,
      });
      await load();
    } catch (reason) {
      setError(String(reason));
    } finally {
      setSaving(null);
    }
  };

  const toggleFeature = async (): Promise<void> => {
    setSaving('feature');
    try {
      await api.setSetting('boss.enabled', !enabled);
      setEnabled(!enabled);
    } catch (reason) {
      setError(String(reason));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <PageTitle title="Boss Break" />
      {error && <ErrorBox text={error} />}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div>
            <div className="display" style={{ fontSize: 18 }}>
              MASTER FEATURE FLAG
            </div>
            <div style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}>
              {items.length} bosses · {totalRuns} issued runs. Keep OFF until
              content and rewards are ready.
            </div>
          </div>
          <button
            className={`btn ${enabled ? 'btn-danger' : 'btn-primary'}`}
            disabled={saving === 'feature'}
            onClick={() => void toggleFeature()}
          >
            {enabled ? 'DISABLE MODE' : 'ENABLE MODE'}
          </button>
        </div>
      </div>

      {competition && competitionStatus && (
        <CompetitionPanel
          config={competition}
          status={competitionStatus}
          frames={frames}
          saving={saving}
          onChange={setCompetition}
          onSave={async () => {
            setSaving('competition');
            setError(null);
            try {
              await api.setBossCompetition(competition);
              await load();
            } catch (reason) {
              setError(String(reason));
            } finally {
              setSaving(null);
            }
          }}
          onSettle={async () => {
            setSaving('competition-settle');
            setError(null);
            try {
              await api.settleBossCompetition();
              await load();
            } catch (reason) {
              setError(String(reason));
            } finally {
              setSaving(null);
            }
          }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          className="btn btn-primary"
          disabled={saving === 'new'}
          onClick={() => void create()}
        >
          + CREATE BOSS
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map((item) => {
          const draft = drafts[item.id]!;
          return (
            <section key={item.id} className="card" style={{ padding: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 14,
                }}
              >
                <div>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: draft.accentColor,
                      marginRight: 8,
                      boxShadow: `0 0 10px ${draft.accentColor}`,
                    }}
                  />
                  <span className="display" style={{ fontSize: 20 }}>
                    {draft.name}
                  </span>
                  <span className="tag" style={{ marginLeft: 10 }}>
                    {item._count.runs} RUNS
                  </span>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={draft.isActive}
                    onChange={(event) =>
                      updateDraft(item.id, 'isActive', event.target.checked)
                    }
                  />
                  ACTIVE
                </label>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
                  gap: 10,
                }}
              >
                <Field label="Name">
                  <input
                    value={draft.name}
                    onChange={(event) =>
                      updateDraft(item.id, 'name', event.target.value)
                    }
                    style={fullW}
                  />
                </Field>
                <Field label="Slug">
                  <input
                    value={draft.slug}
                    onChange={(event) =>
                      updateDraft(item.id, 'slug', event.target.value)
                    }
                    style={fullW}
                  />
                </Field>
                <Field label="Accent">
                  <input
                    type="color"
                    value={draft.accentColor}
                    onChange={(event) =>
                      updateDraft(item.id, 'accentColor', event.target.value)
                    }
                    style={{ ...fullW, height: 40 }}
                  />
                </Field>
                <Field label="Daily rotation">
                  <select
                    value={draft.scheduleDay ?? ''}
                    onChange={(event) =>
                      updateDraft(
                        item.id,
                        'scheduleDay',
                        event.target.value ? Number(event.target.value) : null,
                      )
                    }
                    style={fullW}
                  >
                    <option value="">Outside rotation</option>
                    {[
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ].map((day, index) => (
                      <option key={day} value={index + 1}>
                        {index + 1} · {day}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Boss image URL">
                  <input
                    value={draft.imageUrl ?? ''}
                    onChange={(event) =>
                      updateDraft(
                        item.id,
                        'imageUrl',
                        event.target.value || null,
                      )
                    }
                    style={fullW}
                  />
                </Field>
                <Field label="Hard-clear avatar frame">
                  <select
                    value={draft.rewardFrameId ?? ''}
                    onChange={(event) =>
                      updateDraft(
                        item.id,
                        'rewardFrameId',
                        event.target.value || null,
                      )
                    }
                    style={fullW}
                  >
                    <option value="">No frame</option>
                    {frames.map((frame) => (
                      <option key={frame.id} value={frame.id}>
                        {frame.name}
                      </option>
                    ))}
                  </select>
                </Field>
                {NUMBER_FIELDS.map((field) => (
                  <Field key={field.key} label={field.label}>
                    <input
                      type="number"
                      step={field.step ?? 1}
                      value={draft[field.key] as number}
                      onChange={(event) =>
                        updateDraft(
                          item.id,
                          field.key,
                          Number(event.target.value) as never,
                        )
                      }
                      style={fullW}
                    />
                  </Field>
                ))}
              </div>
              <Field label="Description">
                <textarea
                  rows={2}
                  value={draft.description ?? ''}
                  onChange={(event) =>
                    updateDraft(
                      item.id,
                      'description',
                      event.target.value || null,
                    )
                  }
                  style={{ ...fullW, resize: 'vertical', marginTop: 4 }}
                />
              </Field>
              <button
                className="btn btn-primary"
                style={{ width: '100%', marginTop: 14 }}
                disabled={saving === item.id}
                onClick={() => void save(item.id)}
              >
                {saving === item.id ? 'SAVING…' : 'SAVE BOSS'}
              </button>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function CompetitionPanel({
  config,
  status,
  frames,
  saving,
  onChange,
  onSave,
  onSettle,
}: {
  config: AdminBossCompetitionConfig;
  status: AdminBossCompetitionStatus;
  frames: AdminCosmetic[];
  saving: string | null;
  onChange: (next: AdminBossCompetitionConfig) => void;
  onSave: () => Promise<void>;
  onSettle: () => Promise<void>;
}) {
  const updatePointTier = (
    index: number,
    patch: Partial<AdminBossCompetitionConfig['dailyRankPoints'][number]>,
  ): void => {
    onChange({
      ...config,
      dailyRankPoints: config.dailyRankPoints.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, ...patch } : tier,
      ),
    });
  };
  const updateRewardTier = (
    index: number,
    patch: Partial<AdminBossCompetitionConfig['weeklyRewards'][number]>,
  ): void => {
    onChange({
      ...config,
      weeklyRewards: config.weeklyRewards.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, ...patch } : tier,
      ),
    });
  };
  const inputStyle = { ...fullW, minWidth: 70 };
  return (
    <section className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 14,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 18 }}>
            DAILY PERFECT SCORE → WEEKLY LADDER
          </div>
          <div
            style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}
          >
            Today: {status.dailyPlayers} players ·{' '}
            {status.dailyDamage.toLocaleString()} points. Week:{' '}
            {status.weeklyPlayers} ranked players.
          </div>
          <div className="tag" style={{ marginTop: 6 }}>
            WEEK CLOSES {new Date(status.weekEndsAt).toLocaleString()}
            {!status.rankingOpen ? ' · SETTLING' : ''}
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(event) =>
              onChange({ ...config, enabled: event.target.checked })
            }
          />
          ENABLED
        </label>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Field label="Ranked runs / player / day">
          <input
            type="number"
            min={1}
            max={20}
            value={config.dailyRunLimit}
            onChange={(event) =>
              onChange({ ...config, dailyRunLimit: Number(event.target.value) })
            }
            style={fullW}
          />
        </Field>
        <Field label="Timezone offset (minutes)">
          <input
            type="number"
            min={-720}
            max={840}
            value={config.tzOffsetMinutes}
            onChange={(event) =>
              onChange({
                ...config,
                tzOffsetMinutes: Number(event.target.value),
              })
            }
            style={fullW}
          />
        </Field>
        <Field label="Weekly finalize day">
          <select
            value={config.weeklyFinalizeDay}
            onChange={(event) =>
              onChange({
                ...config,
                weeklyFinalizeDay: Number(event.target.value),
              })
            }
            style={fullW}
          >
            {[
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday',
            ].map((day, index) => (
              <option key={day} value={index + 1}>
                {index + 1} · {day}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Weekly finalize hour">
          <input
            type="number"
            min={0}
            max={23}
            value={config.weeklyFinalizeHour}
            onChange={(event) =>
              onChange({
                ...config,
                weeklyFinalizeHour: Number(event.target.value),
              })
            }
            style={fullW}
          />
        </Field>
      </div>

      <div className="tag" style={{ marginBottom: 8 }}>
        DAILY RANK → WEEKLY POINTS
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {config.dailyRankPoints.map((tier, index) => (
          <div
            key={`points-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr auto',
              gap: 8,
            }}
          >
            <input
              type="number"
              value={tier.from}
              aria-label="Rank from"
              onChange={(event) =>
                updatePointTier(index, { from: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <input
              type="number"
              value={tier.to}
              aria-label="Rank to"
              onChange={(event) =>
                updatePointTier(index, { to: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <input
              type="number"
              value={tier.points}
              aria-label="Weekly points"
              onChange={(event) =>
                updatePointTier(index, { points: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <button
              className="btn btn-danger"
              disabled={config.dailyRankPoints.length === 1}
              onClick={() =>
                onChange({
                  ...config,
                  dailyRankPoints: config.dailyRankPoints.filter(
                    (_, tierIndex) => tierIndex !== index,
                  ),
                })
              }
            >
              ×
            </button>
          </div>
        ))}
        <button
          className="btn"
          onClick={() =>
            onChange({
              ...config,
              dailyRankPoints: [
                ...config.dailyRankPoints,
                { from: 101, to: 200, points: 1 },
              ],
            })
          }
        >
          + ADD POINT BAND
        </button>
      </div>

      <div className="tag" style={{ margin: '16px 0 8px' }}>
        WEEKLY RANK → REWARD
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {config.weeklyRewards.map((tier, index) => (
          <div
            key={`reward-${index}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '0.7fr 0.7fr 1fr 1fr 1.5fr auto',
              gap: 8,
            }}
          >
            <input
              type="number"
              value={tier.from}
              aria-label="Rank from"
              onChange={(event) =>
                updateRewardTier(index, { from: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <input
              type="number"
              value={tier.to}
              aria-label="Rank to"
              onChange={(event) =>
                updateRewardTier(index, { to: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <input
              type="number"
              value={tier.coins}
              aria-label="Coins"
              onChange={(event) =>
                updateRewardTier(index, { coins: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <input
              type="number"
              value={tier.gems}
              aria-label="Gems"
              onChange={(event) =>
                updateRewardTier(index, { gems: Number(event.target.value) })
              }
              style={inputStyle}
            />
            <select
              value={tier.frameId ?? ''}
              aria-label="Avatar frame"
              onChange={(event) =>
                updateRewardTier(index, {
                  frameId: event.target.value || null,
                })
              }
              style={inputStyle}
            >
              <option value="">No frame</option>
              {frames.map((frame) => (
                <option key={frame.id} value={frame.id}>
                  {frame.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-danger"
              disabled={config.weeklyRewards.length === 1}
              onClick={() =>
                onChange({
                  ...config,
                  weeklyRewards: config.weeklyRewards.filter(
                    (_, tierIndex) => tierIndex !== index,
                  ),
                })
              }
            >
              ×
            </button>
          </div>
        ))}
        <button
          className="btn"
          onClick={() =>
            onChange({
              ...config,
              weeklyRewards: [
                ...config.weeklyRewards,
                { from: 101, to: 200, coins: 100, gems: 0, frameId: null },
              ],
            })
          }
        >
          + ADD REWARD BAND
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={saving === 'competition'}
          onClick={() => void onSave()}
        >
          {saving === 'competition' ? 'SAVING…' : 'SAVE LADDER CONFIG'}
        </button>
        <button
          className="btn"
          disabled={saving === 'competition-settle'}
          onClick={() => void onSettle()}
        >
          SETTLE DUE PERIODS
        </button>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="tag">{label}</span>
      {children}
    </label>
  );
}
